/**
 * El formateo, en un solo sitio (SPEC 15.1). Ninguna pantalla lo reimplementa.
 *
 * SPEC 19.19: la zona horaria la decide el SERVIDOR, no el dispositivo.
 * Formatear con la del telefono hizo que las operaciones cambiaran de dia al
 * viajar. La app lee `app.timeZone` de `/lookups` y la pasa aqui.
 */

const CULTURE = "es-GT";

/**
 * El R con dos decimales y SIGNO EXPLICITO: +1.80R, −1.00R (SPEC 15.5). El signo
 * menos es el U+2212, no un guion: en cifras tabulares se alinea con el mas.
 */
export function formatR(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";

  const text = Math.abs(value).toFixed(2);

  if (value > 0) return `+${text}R`;
  if (value < 0) return `−${text}R`;

  return `0.00R`;
}

/** Los pips con dos decimales, sin decimales de mas si son enteros. */
export function formatPips(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";

  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

/**
 * El lotaje, con los decimales que hagan falta y ni uno mas.
 *
 * 0.10 se escribe `0.1` y 1 se escribe `1`: los ceros de relleno a la derecha no
 * dicen nada y en una columna de lotajes distintos solo hacen ruido. Dos
 * decimales son lo normal (0.01 es el micro-lote), pero el contrato admite
 * cuatro y hay brokers que los usan, asi que el tope es cuatro y no dos.
 *
 * Devuelve cadena vacia y no un guion cuando falta: quien llama decide si pinta
 * algo, porque un guion donde no hay lotaje se lee como un cero, y cero lotes no
 * es una operacion pequena sino una que no ocurrio.
 */
export function formatLot(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";

  return value.toLocaleString("es-GT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

/**
 * Los pips de un RESULTADO, con signo explicito como el R: +180, −100, 0. Lo
 * que gano o perdio se lee con el signo delante; el riesgo y el objetivo son
 * distancias y van por `formatPips`, sin el. El menos es el U+2212 por el mismo
 * motivo que en `formatR`: en cifras tabulares se alinea con el mas.
 */
export function formatSignedPips(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";

  const text = formatPips(Math.abs(value));

  if (value > 0) return `+${text}`;
  if (value < 0) return `−${text}`;

  return text;
}

/** Los precios con los decimales del INSTRUMENTO (`pricePrecision`). */
export function formatPrice(
  value: number | null | undefined,
  precision: number,
): string {
  if (value === null || value === undefined) return "—";

  return value.toFixed(precision);
}

/** Un porcentaje a partir de una razon de 0 a 1. */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";

  return `${Math.round(value * 100)} %`;
}

/** Una fecha `yyyy-MM-dd` del servidor, tal cual: ya es el dia del usuario. */
export function formatDay(isoDate: string | null | undefined): string {
  if (!isoDate) return "—";

  const [year, month, day] = isoDate.split("-");

  if (year === undefined || month === undefined || day === undefined) return isoDate;

  return new Intl.DateTimeFormat(CULTURE, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(Number(year), Number(month) - 1, Number(day)));
}

/**
 * El mes y el ano, para el titulo del calendario: "Septiembre de 2026".
 *
 * El dia 1 se construye con el reloj local solo para nombrar el mes; no hay
 * ningun instante en juego, asi que la zona horaria no puede desplazarlo.
 */
export function formatMonth(year: number, month: number): string {
  const text = new Intl.DateTimeFormat(CULTURE, { month: "long", year: "numeric" }).format(
    new Date(year, month - 1, 1),
  );

  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Un instante UTC, en la zona horaria que dijo el SERVIDOR. */
export function formatTime(
  isoInstant: string | null | undefined,
  timeZone: string,
): string {
  if (!isoInstant) return "—";

  return new Intl.DateTimeFormat(CULTURE, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(new Date(isoInstant));
}

export function formatDateTime(
  isoInstant: string | null | undefined,
  timeZone: string,
): string {
  if (!isoInstant) return "—";

  return new Intl.DateTimeFormat(CULTURE, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(new Date(isoInstant));
}

/**
 * Cuanto hace, en DIAS DEL USUARIO: "hoy", "ayer", "hace 4 dias".
 *
 * Lo usa la campana, y ahi la unidad tiene que ser el dia y no la hora: sus
 * avisos se miden en dias —tres abierta, dos sin registrar, siete sin ejecutar—
 * y un "hace 76 horas" obliga a dividir de cabeza para saber si eso es mucho.
 *
 * El dia es el de la ZONA DEL SERVIDOR y no la del dispositivo (SPEC 19.19). Con
 * la del telefono, algo cerrado a las diez de la noche en Guatemala se leeria
 * como "ayer" desde Madrid, que es el mismo error que hacia cambiar de dia a las
 * operaciones al viajar.
 */
export function formatSince(
  isoInstant: string | null | undefined,
  timeZone: string,
  /**
   * El "ahora" contra el que se mide. Existe por los tests: sin el, la unica
   * forma de comprobar el limite de medianoche en Guatemala seria esperar a que
   * el reloj de la maquina llegara ahi.
   */
  now: Date = new Date(),
): string {
  if (!isoInstant) return "—";

  const then = dayNumberIn(new Date(isoInstant), timeZone);
  const today = dayNumberIn(now, timeZone);

  if (then === null || today === null) return "—";

  /* Se redondea porque los dos extremos son medianoches del mismo huso: la
     division es exacta salvo por el salto de horario de verano, que dejaria
     23 o 25 horas entre dos medianoches consecutivas. */
  const days = Math.round((today - then) / 86_400_000);

  if (days <= 0) return "hoy";
  if (days === 1) return "ayer";

  return `hace ${days} dias`;
}

/**
 * El dia civil de un instante en una zona, como milisegundos de su medianoche
 * UTC. Solo sirve para RESTAR dos de ellos, que es justo lo que hace falta: el
 * valor absoluto no representa ningun instante real.
 */
function dayNumberIn(date: Date, timeZone: string): number | null {
  if (Number.isNaN(date.getTime())) return null;

  /* `formatToParts` y no una cadena formateada: el orden de los campos depende
     de la cultura, y partir "11/09/2026" por las barras da el mes por el dia en
     cuanto cambia la cultura. */
  const parts = new Intl.DateTimeFormat(CULTURE, {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const find = (type: string): number | null => {
    const value = parts.find((part) => part.type === type)?.value;
    const parsed = value === undefined ? Number.NaN : Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  };

  const year = find("year");
  const month = find("month");
  const day = find("day");

  if (year === null || month === null || day === null) return null;

  return Date.UTC(year, month - 1, day);
}

/**
 * Parsea lo que el usuario escribe en un campo decimal.
 *
 * Se lee con PUNTO decimal; es-GT usa punto, asi que no hay conversion
 * (SPEC 15.5). Se acepta la coma igualmente porque algunos teclados moviles la
 * ponen sin preguntar.
 */
export function parseDecimal(text: string): number | null {
  const normalized = text.trim().replace(",", ".");

  if (normalized === "") return null;

  const value = Number(normalized);

  return Number.isFinite(value) ? value : null;
}

const OUTCOMES: Record<string, string> = {
  win: "Ganada",
  loss: "Perdida",
  breakEven: "En tablas",
};

const STATUSES: Record<string, string> = {
  planned: "Planificada",
  open: "Abierta",
  closed: "Cerrada",
  cancelled: "Cancelada",
};

const DIRECTIONS: Record<string, string> = {
  buy: "Compra",
  sell: "Venta",
};

/**
 * Que momento retrata una captura (`ImageKind` del contrato, SPEC 14). El orden
 * es el del servidor —antes, durante, despues, y luego los marcos temporales—
 * porque es el orden en que se toman las capturas, no el alfabetico.
 */
const IMAGE_KINDS: Record<string, string> = {
  before: "Antes de entrar",
  during: "Mientras vivia",
  after: "Despues de salir",
  higherTimeframe: "Marco temporal mayor",
  lowerTimeframe: "Marco temporal menor",
  other: "Otra",
};

/** De donde salieron los pips de una operacion (`PipsSource`, ADR 0007). */
const PIPS_SOURCES: Record<string, string> = {
  prices: "Derivados de los precios",
  typed: "Escritos a mano",
};

export function labelPipsSource(value: string | null | undefined): string {
  return value === null || value === undefined ? "—" : (PIPS_SOURCES[value] ?? value);
}

/** Las etiquetas en espanol de las enumeraciones del contrato (SPEC 20). */
export function labelOutcome(value: string | null | undefined): string {
  return value === null || value === undefined ? "—" : (OUTCOMES[value] ?? value);
}

export function labelStatus(value: string): string {
  return STATUSES[value] ?? value;
}

export function labelDirection(value: string): string {
  return DIRECTIONS[value] ?? value;
}

export function labelImageKind(value: string | null | undefined): string {
  return value === null || value === undefined ? "Otra" : (IMAGE_KINDS[value] ?? value);
}

/** La clase de la insignia del resultado: `win`, `loss` o `be`. */
export function badgeOf(outcome: string | null | undefined): string {
  if (outcome === "win") return "win";

  return outcome === "loss" ? "loss" : "be";
}

/**
 * La clase de color del R: verde si gana, rojo si pierde, apagado si esta en
 * tablas o no hay R. Nunca ambar: el ambar esta reservado a la regla
 * innegociable rota (ADR 0003, punto conservado de SPEC 16).
 */
export function toneOf(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) return "muted";

  return value > 0 ? "up" : "down";
}

/**
 * Bytes en la unidad que se lee de un vistazo, en base 1024, que es la que usa
 * `pg_total_relation_size` y la que ensena el sistema de archivos.
 *
 * Con un decimal desde los megabytes y sin decimales en bytes: "14.4 MB" dice
 * lo que hay que saber y "15 099 494 B" no dice nada sin contarlo con el dedo.
 */
const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

export function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";

  let size = Math.abs(value);
  let unit = 0;

  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }

  const decimals = unit === 0 ? 0 : size < 10 ? 1 : unit === 1 ? 0 : 1;
  const sign = value < 0 ? "−" : "";

  return `${sign}${size.toFixed(decimals)} ${BYTE_UNITS[unit] ?? "B"}`;
}

/**
 * Un recuento de filas con separador de miles. Catorce mil quinientas setenta y
 * nueve filas escritas como 14579 se leen mal justo en la pantalla que existe
 * para comparar magnitudes.
 */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";

  return new Intl.NumberFormat(CULTURE).format(value);
}

/** Milisegundos, en segundos cuando pasan del segundo: "1.03 s", "843 ms". */
export function formatElapsed(milliseconds: number | null | undefined): string {
  if (milliseconds === null || milliseconds === undefined || !Number.isFinite(milliseconds)) {
    return "—";
  }

  return milliseconds >= 1000
    ? `${(milliseconds / 1000).toFixed(2)} s`
    : `${formatCount(Math.round(milliseconds))} ms`;
}

/** Los tres roles del ADR 0005, en espanol y con la etiqueta de la plantilla. */
const ROLES: Record<string, string> = {
  administrator: "Administrador",
  trader: "Trader",
  mentor: "Mentor",
};

export function labelRole(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";

  return ROLES[value] ?? value;
}

/**
 * Las iniciales del `.avatar` de la plantilla: la primera letra de las dos
 * primeras palabras.
 *
 * Se parte por espacios y no por los dos primeros caracteres porque "Ana Lopez"
 * tiene que dar "AL" y no "AN". Si no hay nada legible se devuelve "TJ", que es
 * lo que la plantilla pintaba antes de que hubiera usuarios: un avatar vacio se
 * lee como un fallo de carga.
 */
export function initials(name: string | null | undefined): string {
  if (name === null || name === undefined) return "TJ";

  const words = name
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word !== "");

  const letters = words
    .slice(0, 2)
    .map((word) => word.slice(0, 1))
    .join("");

  return letters === "" ? "TJ" : letters.toLocaleUpperCase(CULTURE);
}

/**
 * Minutos en palabras para el aviso de inactividad: "30 minutos", "1 hora",
 * "1 h 30 min". Un "90 minutos" obliga a dividir de cabeza para saber si es
 * mucho o poco.
 */
export function formatMinutes(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";

  const minutes = Math.round(value);

  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minuto" : "minutos"}`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes - hours * 60;
  const hoursText = `${hours} ${hours === 1 ? "hora" : "horas"}`;

  return rest === 0 ? hoursText : `${hoursText} y ${rest} min`;
}
